import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MdMic, MdMicNone } from "react-icons/md";
import axios from "axios";
import PacmanLoader from "react-spinners/PacmanLoader";
import useAuth from "../../Users/hooks/useAuth";

const scoreColor = (score) => {
  if (typeof score !== "number") return "bg-slate-50 border-slate-200 text-slate-700";
  if (score >= 80) return "bg-green-50 border-green-200 text-green-800";
  if (score >= 40) return "bg-amber-50 border-amber-200 text-amber-800";
  return "bg-red-50 border-red-200 text-red-800";
};

const scoreBadge = (score) => {
  if (typeof score !== "number") return "bg-slate-400";
  if (score >= 80) return "bg-green-600";
  if (score >= 40) return "bg-amber-600";
  return "bg-red-600";
};

const phonemeDot = (score) => {
  if (typeof score !== "number") return "bg-slate-300 border-slate-400";
  if (score >= 80) return "bg-green-500 border-green-600";
  if (score >= 40) return "bg-amber-500 border-amber-600";
  return "bg-red-500 border-red-600";
};

const metricTone = (value) => {
  if (typeof value !== "number") return "text-blue-700";
  if (value >= 80) return "text-green-700";
  if (value >= 40) return "text-amber-700";
  return "text-red-700";
};

const formatKey = (key) => key.replace(/([A-Z])/g, " $1").trim();

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const encodePath = (value) => encodeURIComponent(String(value ?? ""));

const resolveAudioUrl = (audioUrl) => {
  if (!audioUrl) return "";
  if (/^https?:\/\//i.test(audioUrl)) return audioUrl;
  return `${process.env.REACT_APP_USER_API_URL}${audioUrl}`;
};

const getPublishedExercisePath = (recordingId) => {
  if (!recordingId) return "";
  return `/exercise/${encodeURIComponent(String(recordingId))}`;
};

const getPublishedExerciseUrl = (recordingId) => {
  const path = getPublishedExercisePath(recordingId);

  if (!path) return "";
  if (typeof window === "undefined") return path;

  return `${window.location.origin}${path}`;
};

const getAttemptAssessment = (attempt) => {
  if (!attempt) return null;

  if (attempt.user) {
    return { user: attempt.user };
  }

  if (attempt.analysis?.ref) {
    return { user: attempt.analysis.ref };
  }

  if (attempt.analysis?.NBest) {
    return { user: attempt.analysis };
  }

  if (attempt.pronunciation) {
    return { pronunciation: attempt.pronunciation };
  }

  return null;
};

const getAttemptPronScore = (attempt) => {
  const assessment = getAttemptAssessment(attempt);
  const nBest =
    assessment?.user?.NBest?.[0] ??
    assessment?.pronunciation?.NBest?.[0] ??
    null;

  return nBest?.PronunciationAssessment?.PronScore;
};

const formatAttemptTime = (attempt) => {
  const raw = attempt?.ts;
  if (!raw) return "Saved attempt";

  const date = new Date(Number(raw) * 1000);
  if (Number.isNaN(date.getTime())) return "Saved attempt";

  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    day: "numeric",
    month: "short",
  }).format(date);
};

const normalizeAttemptsByIndex = (payload) => {
  const source = payload?.attempts_by_index || payload?.attemptsByIndex || {};
  const next = {};

  Object.entries(source).forEach(([index, attempts]) => {
    if (!Array.isArray(attempts)) return;

    next[String(index)] = attempts
      .map((attempt, attemptIndex) => ({
        ...attempt,
        attempt_id:
          attempt.attempt_id ||
          attempt.audio_id ||
          `${index}-${attempt.ts || Date.now()}-${attemptIndex}`,
        selected: Boolean(attempt.selected),
      }))
      .sort((a, b) => Number(a.ts || 0) - Number(b.ts || 0));
  });

  return next;
};

const getAuthL1Language = (auth) => {
  if (!auth) return null;

  const directKeys = [
    "l1_language",
    "l1Language",
    "native_language",
    "nativeLanguage",
    "first_language",
    "firstLanguage",
    "mother_tongue",
    "motherTongue",
    "mother_language",
    "motherLanguage",
    "l1",
    "L1",
    "language_1",
    "language1",
    "primary_language",
    "primaryLanguage",
  ];

  for (const key of directKeys) {
    if (typeof auth[key] === "string" && auth[key].trim()) return auth[key].trim();
  }

  if (auth.profile) {
    for (const key of directKeys) {
      if (typeof auth.profile[key] === "string" && auth.profile[key].trim()) {
        return auth.profile[key].trim();
      }
    }
  }

  if (Array.isArray(auth.languages)) {
    const nativeEntry = auth.languages.find((entry) => {
      if (!entry || typeof entry !== "object") return false;
      const role = String(entry.role || entry.type || "").toLowerCase();
      return entry.is_l1 || entry.native || entry.mother_tongue || ["l1", "native", "mother_tongue", "first_language"].includes(role);
    });

    if (nativeEntry) {
      return nativeEntry.language || nativeEntry.name || nativeEntry.code || null;
    }
  }

  return null;
};

const Slideshow = ({
  transcript,
  title,
  version,
  language,
  description = "",
  exclusiveSharedRecordings = [],
  onClose,
  onSharedRecording = null,
  onUnsharedRecording = null,
}) => {
  const { auth } = useAuth();
  const cleanDescription = String(description || "").trim();

  const [segments, setSegments] = useState([]);
  const [page, setPage] = useState(1);
  const [segment, setSegment] = useState("");

  const [isgranted, setIsGranted] = useState(false);
  const [stream, setStream] = useState(null);

  const [localAudioUrl, setLocalAudioUrl] = useState("");
  const [assessment, setAssessment] = useState(null);
  const [attemptsByIndex, setAttemptsByIndex] = useState({});
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [attemptsError, setAttemptsError] = useState("");
  const [selectingAttemptId, setSelectingAttemptId] = useState("");

  const [isPlayingAll, setIsPlayingAll] = useState(false);
  const [playAllStatus, setPlayAllStatus] = useState("");

  const [isCreatingShare, setIsCreatingShare] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [shareNeedsUpdate, setShareNeedsUpdate] = useState(false);
  const [shareError, setShareError] = useState("");
  const [shareCopied, setShareCopied] = useState(false);

  const [loading, setLoading] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState("inactive");
  const [recordingError, setRecordingError] = useState("");

  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [viewMode, setViewMode] = useState("auto");

  const [showDescription, setShowDescription] = useState(false);
  const [showTakes, setShowTakes] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  const mediaRecorder = useRef(null);
  const audioChunksRef = useRef([]);
  const pulseRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioUrlCacheRef = useRef({});
  const allPlaybackRef = useRef({ shouldStop: false, audio: null });

  const maxpage = Math.max(segments.length, 1);
  const progressPercent = maxpage > 0 ? (page / maxpage) * 100 : 0;
  const isFirst = page <= 1;
  const isLast = page >= maxpage;
  const isRecording = recordingStatus === "recording";

  const pageKey = String(page);
  const currentAttempts = attemptsByIndex[pageKey] || [];
  const selectedAttempt = currentAttempts.find((attempt) => attempt.selected) || null;
  const latestAttempt = currentAttempts[currentAttempts.length - 1] || null;
  const activeAttempt = selectedAttempt || latestAttempt;
  const recordedSentenceCount = Object.values(attemptsByIndex).filter((attempts) => attempts?.length > 0).length;
  const selectedSentenceCount = Object.values(attemptsByIndex).filter((attempts) => attempts?.some((attempt) => attempt.selected)).length;

  const selectedAttemptsInOrder = useMemo(() => {
    return segments
      .map((text, idx) => {
        const sentenceIndex = String(idx + 1);
        const attempts = attemptsByIndex[sentenceIndex] || [];
        const selected = attempts.find((attempt) => attempt.selected);

        if (!selected) return null;

        return {
          ...selected,
          index: sentenceIndex,
          sentenceNumber: idx + 1,
          text,
        };
      })
      .filter(Boolean);
  }, [attemptsByIndex, segments]);

  const canPlayAllSelected = selectedAttemptsInOrder.length > 0 && !isRecording && !loading;

  const fullRecordingReady = maxpage > 0 && selectedSentenceCount >= maxpage;
  const fullRecordingPercent = maxpage > 0 ? (selectedSentenceCount / maxpage) * 100 : 0;
  const missingSelectedIndexes = segments
    .map((_, idx) => String(idx + 1))
    .filter((index) => !(attemptsByIndex[index] || []).some((attempt) => attempt.selected));

  const otherSharedRecordings = Array.isArray(exclusiveSharedRecordings)
    ? exclusiveSharedRecordings.filter((item) => item && String(item.version) !== String(version))
    : [];
  const otherSharedLabel = otherSharedRecordings
    .map((item) => item.version_label || `version ${item.version}`)
    .filter(Boolean)
    .join(", ");

  const currentSentenceState = selectedAttempt
    ? "selected"
    : currentAttempts.length > 0
      ? "needs-choice"
      : "empty";

  const selectedAudioUrl = resolveAudioUrl(activeAttempt?.audio_url);
  const canPlay = Boolean(localAudioUrl || selectedAudioUrl) && !isRecording;

  const activeAssessment = assessment || getAttemptAssessment(activeAttempt);

  const userNBest =
    activeAssessment?.user?.NBest?.[0] ??
    activeAssessment?.pronunciation?.NBest?.[0] ??
    null;

  const hasUserAssessment = Boolean(userNBest?.PronunciationAssessment);
  const wordsRaw = Array.isArray(userNBest?.Words) ? userNBest.Words : [];
  const wordCount = wordsRaw.length;
  const isLong = wordCount > 10;

  const wordItems = wordsRaw
    .map((w, idx) => ({ w, idx }))
    .filter(({ w }) => w?.PronunciationAssessment?.ErrorType !== "Omission");

  const filteredWords = showOnlyErrors
    ? wordItems.filter(({ w }) => (w?.PronunciationAssessment?.AccuracyScore ?? 0) < 80)
    : wordItems;

  const visibleWords = filteredWords;
  const effectiveView = viewMode === "auto" ? (isLong ? "chips" : "grid") : viewMode;
  const overallScore = userNBest?.PronunciationAssessment?.PronScore;

  const fetchPresentationAttempts = useCallback(async () => {
    const token = localStorage.getItem("token");
    const url = `${process.env.REACT_APP_USER_API_URL}/presentation/presentation_recordings/${encodePath(language)}/${encodePath(title)}/${encodePath(version)}`;

    setAttemptsLoading(true);
    setAttemptsError("");

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Could not load attempts: ${response.status}`);
      }

      const payload = await response.json();
      setAttemptsByIndex(normalizeAttemptsByIndex(payload));
    } catch (error) {
      console.error("Could not load recording attempts:", error);
      setAttemptsError("Could not load saved recording attempts.");
    } finally {
      setAttemptsLoading(false);
    }
  }, [language, title, version]);

  const sendBlob = useCallback(({ blob, title, version, language, index, segment, localUrl }) => {
    if (loading) return;

    const token = localStorage.getItem("token");
    const url = `${process.env.REACT_APP_USER_API_URL}/presentation/upload_blob_presentation/${encodePath(language)}/${encodePath(title)}/${encodePath(version)}/${encodePath(index)}`;
    const formData = new FormData();

    formData.append("uploaded_file", blob);
    formData.append("sendText", JSON.stringify({ reference: segment }));
    formData.append("tz", Intl.DateTimeFormat().resolvedOptions().timeZone);

    const config = {
      headers: {
        "content-type": blob.type,
        Authorization: `Bearer ${token}`,
      },
    };

    setLoading(true);

    axios
      .post(url, formData, config)
      .then((response) => {
        const payload = response.data || {};
        const key = String(index);
        const attempt = payload.attempt || {
          attempt_id: payload.attempt_id || payload.audio_id || `${index}-${Date.now()}`,
          audio_id: payload.audio_id,
          audio_url: payload.audio_url,
          index: key,
          text: segment,
          ts: Math.floor(Date.now() / 1000),
          selected: true,
          user: payload.user,
          analysis: payload.user ? { ref: payload.user } : undefined,
        };

        const normalizedAttempt = {
          ...attempt,
          attempt_id: attempt.attempt_id || attempt.audio_id || `${key}-${Date.now()}`,
          selected: true,
        };

        // A fresh take replaces the previous selected take for this sentence in
        // the full recording. This keeps the "full recording" aligned with the
        // user's newest recording unless they explicitly choose an older take.
        fetch(`${process.env.REACT_APP_USER_API_URL}/presentation/select_presentation_recording`, {
          method: "POST",
          body: JSON.stringify({
            title,
            language,
            version,
            index: key,
            attempt_id: normalizedAttempt.attempt_id,
            audio_id: normalizedAttempt.audio_id,
          }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }).catch((selectError) => {
          console.error("Could not auto-select the newest take:", selectError);
        });

        setAssessment(payload.user ? { user: payload.user } : getAttemptAssessment(normalizedAttempt));
        setLocalAudioUrl(localUrl);
        setShareNeedsUpdate(true);
        setShareCopied(false);

        setAttemptsByIndex((prev) => {
          const existing = prev[key] || [];

          const nextAttempts = existing.some((item) => item.attempt_id === normalizedAttempt.attempt_id)
            ? existing.map((item) => ({
                ...item,
                selected: item.attempt_id === normalizedAttempt.attempt_id || item.audio_id === normalizedAttempt.audio_id,
              }))
            : [
                ...existing.map((item) => ({ ...item, selected: false })),
                normalizedAttempt,
              ];

          return {
            ...prev,
            [key]: nextAttempts,
          };
        });
      })
      .catch((error) => {
        console.error("Error uploading file:", error);
        setAssessment(null);
        setRecordingError("Could not analyze this recording. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [language, loading, title, version]);

  const getMicrophonePermission = async () => {
    if (!("MediaRecorder" in window)) {
      setRecordingError("Your browser does not support audio recording.");
      return;
    }

    try {
      const streamData = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      setIsGranted(true);
      setStream(streamData);
      setRecordingError("");
    } catch (err) {
      console.error("Microphone permission error:", err.message);
      setRecordingError("Microphone permission was not granted.");
    }
  };

  useEffect(() => {
    if (navigator.mediaDevices) {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((streamData) => {
          setIsGranted(true);
          setStream(streamData);
          setRecordingError("");
        })
        .catch((err) => {
          console.log("Microphone permission not granted:", err.message);
        });
    }

    return () => {
      if (recordingTimerRef.current) clearTimeout(recordingTimerRef.current);
      if (mediaRecorder.current?.state === "recording") {
        mediaRecorder.current.stop();
      }

      allPlaybackRef.current.shouldStop = true;
      if (allPlaybackRef.current.audio) {
        try {
          allPlaybackRef.current.audio.pause();
          allPlaybackRef.current.audio.currentTime = 0;
        } catch {
          // ignore cleanup errors
        }
      }

      Object.values(audioUrlCacheRef.current).forEach((objectUrl) => {
        try {
          URL.revokeObjectURL(objectUrl);
        } catch {
          // ignore cleanup errors
        }
      });
    };
  }, []);

  useEffect(() => {
    if (Array.isArray(transcript?.segments)) {
      setSegments(transcript.segments);
      setPage(1);
    } else {
      setSegments([]);
      setPage(1);
    }
  }, [transcript, version, language, title]);

  useEffect(() => {
    fetchPresentationAttempts();
  }, [fetchPresentationAttempts]);

  useEffect(() => {
    const nextSegment = segments[page - 1] || "";
    setSegment(nextSegment);
    setAssessment(null);
    setLocalAudioUrl("");
    setRecordingError("");
  }, [page, segments, version]);

  const goToPage = (nextPage) => {
    setPage((current) => clamp(typeof nextPage === "function" ? nextPage(current) : nextPage, 1, maxpage));
  };

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorder.current;

    if (!recorder || recorder.state !== "recording") return;

    if (recordingTimerRef.current) clearTimeout(recordingTimerRef.current);
    if (pulseRef.current) {
      pulseRef.current.getAnimations().forEach((animation) => animation.cancel());
    }

    setRecordingStatus("inactive");
    recorder.stop();
  }, []);

  const startRecording = async () => {
    if (!stream || isRecording || loading || isPlayingAll) return;

    try {
      setRecordingError("");
      setAssessment(null);
      setLocalAudioUrl("");
      audioChunksRef.current = [];

      const options = MediaRecorder.isTypeSupported?.("audio/webm")
        ? { mimeType: "audio/webm" }
        : undefined;

      const media = new MediaRecorder(stream, options);
      mediaRecorder.current = media;

      media.ondataavailable = (event) => {
        if (!event?.data || event.data.size === 0) return;
        audioChunksRef.current.push(event.data);
      };

      media.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const audioUrl = URL.createObjectURL(audioBlob);

        sendBlob({ blob: audioBlob, language, title, version, segment, index: page, localUrl: audioUrl });

        audioChunksRef.current = [];
      };

      media.start();
      setRecordingStatus("recording");

      recordingTimerRef.current = setTimeout(() => {
        if (mediaRecorder.current?.state === "recording") {
          stopRecording();
        }
      }, 30000);

      if (pulseRef.current) {
        pulseRef.current.animate(
          [{ transform: "scale(1.08)" }, { transform: "scale(1.0)" }],
          { duration: 650, iterations: Infinity },
        );
      }
    } catch (err) {
      console.error("Could not start recording:", err);
      setRecordingStatus("inactive");
      setRecordingError("Could not start recording. Try enabling the microphone again.");
    }
  };

  const getPlayableAudioUrl = async (targetUrl) => {
    if (!targetUrl) return "";

    if (targetUrl.startsWith("blob:") || targetUrl.startsWith("data:")) {
      return targetUrl;
    }

    if (!audioUrlCacheRef.current[targetUrl]) {
      const token = localStorage.getItem("token");

      const response = await fetch(targetUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Audio request failed with status ${response.status}`);
      }

      const audioBlob = await response.blob();
      audioUrlCacheRef.current[targetUrl] = URL.createObjectURL(audioBlob);
    }

    return audioUrlCacheRef.current[targetUrl];
  };

  const playRecording = async (url = null) => {
    const targetUrl = url || localAudioUrl || selectedAudioUrl;
    if (!targetUrl) return;

    try {
      const playableUrl = await getPlayableAudioUrl(targetUrl);
      const audioElement = new Audio(playableUrl);
      await audioElement.play();
    } catch (error) {
      console.error("Error playing audio:", error);
      setRecordingError("Could not play this recording. Please try again.");
    }
  };

  const waitForAudioToEnd = (audioElement) => {
    return new Promise((resolve, reject) => {
      audioElement.onended = resolve;
      audioElement.onerror = reject;
    });
  };

  const stopPlayAllSelected = () => {
    allPlaybackRef.current.shouldStop = true;

    if (allPlaybackRef.current.audio) {
      try {
        allPlaybackRef.current.audio.pause();
        allPlaybackRef.current.audio.currentTime = 0;
      } catch {
        // ignore playback cleanup errors
      }
    }

    setIsPlayingAll(false);
    setPlayAllStatus("");
  };

  const playAllSelected = async () => {
    if (!canPlayAllSelected || isPlayingAll) return;

    if (selectedAttemptsInOrder.length === 0) {
      setAttemptsError("No selected recordings yet.");
      return;
    }

    allPlaybackRef.current.shouldStop = false;
    allPlaybackRef.current.audio = null;
    setIsPlayingAll(true);
    setAttemptsError("");

    try {
      for (let i = 0; i < selectedAttemptsInOrder.length; i += 1) {
        if (allPlaybackRef.current.shouldStop) break;

        const attempt = selectedAttemptsInOrder[i];
        const sentenceNumber = Number(attempt.sentenceNumber || attempt.index || i + 1);
        const rawUrl = resolveAudioUrl(attempt.audio_url);

        if (!rawUrl) continue;

        setPlayAllStatus(`Reviewing full recording · sentence ${sentenceNumber} of ${maxpage} (${i + 1}/${selectedAttemptsInOrder.length})`);

        // Move the practice card to the sentence currently being reviewed.
        setPage(sentenceNumber);
        setAssessment(getAttemptAssessment(attempt));
        setLocalAudioUrl("");

        const playableUrl = await getPlayableAudioUrl(rawUrl);

        if (allPlaybackRef.current.shouldStop) break;

        const audioElement = new Audio(playableUrl);
        allPlaybackRef.current.audio = audioElement;

        await audioElement.play();
        await waitForAudioToEnd(audioElement);

        allPlaybackRef.current.audio = null;

        // A tiny pause makes the review feel less abrupt.
        await new Promise((resolve) => setTimeout(resolve, 250));
      }

      if (!allPlaybackRef.current.shouldStop) {
        setPlayAllStatus(`Finished reviewing ${selectedAttemptsInOrder.length} selected take${selectedAttemptsInOrder.length === 1 ? "" : "s"}.`);
      }
    } catch (error) {
      console.error("Could not play selected recordings:", error);
      setAttemptsError("Could not play all selected recordings.");
      setPlayAllStatus("");
    } finally {
      allPlaybackRef.current.audio = null;
      allPlaybackRef.current.shouldStop = false;
      setIsPlayingAll(false);
    }
  };

  const buildUsersRecordingJson = () => {
    const now = new Date().toISOString();
    const randomPart =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    const recordingId = `rec_${randomPart}`;

    const items = selectedAttemptsInOrder
      .filter((attempt) => attempt?.audio_id || attempt?.audio_url)
      .map((attempt) => {
        const index = String(attempt.index || attempt.sentenceNumber || "");
        const sentenceIndex = Number(index);
        const assessment = getAttemptAssessment(attempt);
        const nBest =
          assessment?.user?.NBest?.[0] ??
          assessment?.pronunciation?.NBest?.[0] ??
          null;

        return {
          index,
          text: attempt.text || segments[sentenceIndex - 1] || "",
          attempt_id: attempt.attempt_id || null,
          audio_id: attempt.audio_id || null,
          audio_url: attempt.audio_url || null,
          ts: attempt.ts || null,
          date: attempt.date || null,
          selected: true,

          // Useful summary for the future public page / feed card.
          pronunciation: nBest?.PronunciationAssessment || null,

          // Keep the full assessment available for the future page.
          // Backend can later decide whether to store all of this or strip it.
          assessment: attempt.user || attempt.analysis?.ref || attempt.analysis || null,
        };
      });

    const selectedIndexes = items.map((item) => item.index);
    const allIndexes = segments.map((_, idx) => String(idx + 1));
    const missingIndexes = allIndexes.filter((index) => !selectedIndexes.includes(index));
    const publicUrl = getPublishedExerciseUrl(recordingId);

    return {
      collection: "users_recordings",
      recording_id: recordingId,
      kind: "recorded_text",
      source: "presentation_practice",
      status: "draft_frontend_only",
      visibility: "link",

      owner: {
        username: auth?.username || null,
        l1_language: getAuthL1Language(auth),
      },

      title,
      language,
      version: String(version),
      description: cleanDescription || null,

      created_at: now,
      updated_at: now,

      public_url: publicUrl,

      total_segments: segments.length,
      item_count: items.length,
      selected_indexes: selectedIndexes,
      missing_indexes: missingIndexes,
      is_complete: missingIndexes.length === 0,

      // This is the important part:
      // all selected non-empty segment recordings + their corresponding texts.
      items,
    };
  };

  const createShareLink = async () => {
    if (isCreatingShare || selectedAttemptsInOrder.length === 0) return;

    // If the visible link is already current, there is nothing to update.
    if (shareInfo?.share_url && !shareNeedsUpdate) return;

    const token = localStorage.getItem("token");

    setIsCreatingShare(true);
    setShareError("");
    setShareCopied(false);
    try {
      const nextRecordingJson = buildUsersRecordingJson();
      const usersRecordingJson = {
        ...nextRecordingJson,
        recording_id: shareInfo?.recording_id || nextRecordingJson.recording_id,
        replace_existing: Boolean(shareNeedsUpdate),
        exclusive_share: true,
        unshare_other_versions: true,
      };

      if (!usersRecordingJson.items.length) {
        throw new Error("No selected recordings with audio were found.");
      }

      for (const previousShare of otherSharedRecordings) {
        const unshareResponse = await fetch(`${process.env.REACT_APP_USER_API_URL}/presentation/unshare-recording`, {
          method: "POST",
          body: JSON.stringify({
            title: previousShare.title || title,
            language: previousShare.language || language,
            version: previousShare.version,
            recording_id: previousShare.recording_id || "",
            share_url: previousShare.share_url || "",
            reason: "replaced_by_another_transcript_version",
            replacing_with_version: String(version),
          }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!unshareResponse.ok) {
          throw new Error(`Could not unshare previous version: ${unshareResponse.status}`);
        }

        if (typeof onUnsharedRecording === "function") {
          onUnsharedRecording({
            title: previousShare.title || title,
            language: previousShare.language || language,
            version: previousShare.version,
            recording_id: previousShare.recording_id || "",
            share_url: previousShare.share_url || "",
          });
        }
      }

      const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/presentation/share-recording`, {
        method: "POST",
        body: JSON.stringify(usersRecordingJson),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Could not save shared recording: ${response.status}`);
      }

      const payload = await response.json();
      const finalRecordingId = payload.recording_id || usersRecordingJson.recording_id;
      const publicUrl = getPublishedExerciseUrl(finalRecordingId);

      const nextShareInfo = {
        recording_id: finalRecordingId,
        share_url: publicUrl,
        exercise_url: publicUrl,
        exercise_path: getPublishedExercisePath(finalRecordingId),
        payload: {
          ...(payload.recording || usersRecordingJson),
          recording_id: finalRecordingId,
          public_url: publicUrl,
          share_url: publicUrl,
        },
        item_count: payload.item_count ?? usersRecordingJson.item_count,
        total_segments: payload.total_segments ?? usersRecordingJson.total_segments,
        is_complete: payload.is_complete ?? usersRecordingJson.is_complete,
        already_exists: Boolean(payload.already_exists),
        created: Boolean(payload.created),
        updated: Boolean(payload.updated),
        speaker_l1_language: payload.speaker_l1_language || payload.recording?.speaker_l1_language || null,
        is_native_speaker: Boolean(payload.is_native_speaker || payload.recording?.is_native_speaker),
        speaker_status: payload.speaker_status || payload.recording?.speaker_status || null,
        speaker_label: payload.speaker_label || payload.recording?.speaker_label || null,
      };

      setShareInfo(nextShareInfo);
      setShareNeedsUpdate(false);

      if (typeof onSharedRecording === "function") {
        onSharedRecording({
          title,
          language,
          version,
          shareInfo: nextShareInfo,
          unsharedVersions: [
            ...otherSharedRecordings.map((item) => item.version),
            ...(Array.isArray(payload.unshared_versions) ? payload.unshared_versions : []),
            ...(Array.isArray(payload.unsharedVersions) ? payload.unsharedVersions : []),
          ],
        });
      }

      console.log(
        payload.already_exists
          ? "sharedrecordings existing link"
          : "sharedrecordings saved",
        payload,
      );
    } catch (error) {
      console.error("Could not save shared recording:", error);
      setShareError(
        otherSharedRecordings.length > 0
          ? "Could not replace the currently shared version. Please try again."
          : "Could not save this shared recording. Please try again.",
      );
    } finally {
      setIsCreatingShare(false);
    }
  };

  const copyShareLink = async () => {
    const exerciseUrl = shareInfo?.exercise_url || shareInfo?.share_url;

    if (!exerciseUrl) return;

    try {
      await navigator.clipboard.writeText(exerciseUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 1800);
    } catch (error) {
      console.error("Could not copy exercise link:", error);
      setShareError("Could not copy the exercise link automatically.");
    }
  };

  const openSharedExercise = () => {
    const exerciseUrl = shareInfo?.exercise_url || shareInfo?.share_url;

    if (!exerciseUrl || typeof window === "undefined") return;

    window.open(exerciseUrl, "_blank", "noopener,noreferrer");
  };

  const selectAttempt = async (attempt) => {
    if (!attempt?.attempt_id || selectingAttemptId) return;

    const token = localStorage.getItem("token");
    const url = `${process.env.REACT_APP_USER_API_URL}/presentation/select_presentation_recording`;

    setSelectingAttemptId(attempt.attempt_id);
    setAttemptsError("");

    try {
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify({
          title,
          language,
          version,
          index: String(page),
          attempt_id: attempt.attempt_id,
          audio_id: attempt.audio_id,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Could not select attempt: ${response.status}`);
      }

      setAttemptsByIndex((prev) => {
        const key = String(page);
        const existing = prev[key] || [];

        return {
          ...prev,
          [key]: existing.map((item) => ({
            ...item,
            selected: item.attempt_id === attempt.attempt_id || item.audio_id === attempt.audio_id,
          })),
        };
      });

      setAssessment(getAttemptAssessment(attempt));
      setLocalAudioUrl("");
      setShareNeedsUpdate(true);
      setShareCopied(false);
    } catch (error) {
      console.error("Could not select recording attempt:", error);
      setAttemptsError("Could not mark this attempt as selected.");
    } finally {
      setSelectingAttemptId("");
    }
  };

  const selectedAttemptIndex = currentAttempts.findIndex((attempt) => attempt.selected);
  const selectedTakeNumber = selectedAttemptIndex >= 0 ? selectedAttemptIndex + 1 : null;
  const selectedPronScore = getAttemptPronScore(selectedAttempt);
  const activePronScore = getAttemptPronScore(activeAttempt);

  const currentTakeHeadline = selectedTakeNumber
    ? `Take ${selectedTakeNumber} is used in the full recording`
    : currentAttempts.length > 0
      ? "Choose a take for the full recording"
      : "No take recorded yet";

  const currentTakeSubline = currentAttempts.length > 0
    ? selectedTakeNumber
      ? `${currentAttempts.length} take${currentAttempts.length === 1 ? "" : "s"} saved for this sentence.`
      : "There are saved takes, but none is selected for the full recording."
    : "Record this sentence to create the first take.";

  const sentenceStatusFor = (sentenceIndex) => {
    const attempts = attemptsByIndex[String(sentenceIndex)] || [];
    const hasSelected = attempts.some((attempt) => attempt.selected);
    const hasTakes = attempts.length > 0;

    if (hasSelected) {
      return {
        label: "Selected",
        short: "✓",
        className: "border-green-200 bg-green-50 text-green-700",
      };
    }

    if (hasTakes) {
      return {
        label: "Needs choice",
        short: "◐",
        className: "border-amber-200 bg-amber-50 text-amber-700",
      };
    }

    return {
      label: "Not recorded",
      short: "—",
      className: "border-slate-200 bg-white text-slate-400",
    };
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-blue-50/30 text-slate-950">
      <header className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/95 shadow-sm backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 items-center justify-center rounded-2xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-blue-100"
            >
              ← Back
            </button>

            <div className="min-w-0">
              <h1 className="truncate text-base font-black tracking-tight sm:text-lg">{title}</h1>
              <p className="truncate text-xs font-medium text-slate-500">
                {language} · {maxpage} sentence{maxpage === 1 ? "" : "s"}
              </p>
                          </div>
          </div>

          <div className="hidden min-w-[220px] text-right sm:block">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">Narration</p>
            <p className="text-sm font-semibold text-slate-700">
              Sentence {page} of {maxpage} · Recorded {recordedSentenceCount}/{maxpage}
            </p>
          </div>
        </div>
      </header>

      <main className="mx-auto grid w-full max-w-7xl gap-6 px-4 py-6 lg:grid-cols-[310px_minmax(0,1fr)]">
        <aside className="space-y-5 lg:sticky lg:top-[82px] lg:self-start">
          <section className="relative overflow-hidden rounded-[30px] border border-green-100 bg-white shadow-[0_24px_70px_-48px_rgba(22,163,74,0.78)] ring-1 ring-green-50">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-green-500 via-emerald-400 to-blue-500" />

            <div className="bg-gradient-to-br from-white via-green-50/60 to-blue-50/50 px-5 py-5">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-green-700">Full recording</p>
              <h2 className="mt-1 text-xl font-black tracking-tight">
                {fullRecordingReady
                  ? `Ready · ${selectedSentenceCount}/${maxpage}`
                  : `${selectedSentenceCount}/${maxpage} ready`}
              </h2>

              <div className="mt-4 h-2 overflow-hidden rounded-full bg-white ring-1 ring-inset ring-green-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-green-500 to-blue-500 transition-all duration-300"
                  style={{ width: `${fullRecordingPercent}%` }}
                />
              </div>

              {!fullRecordingReady && missingSelectedIndexes.length > 0 && (
                <p className="mt-3 text-sm font-semibold text-amber-700">
                  Missing selected take: {missingSelectedIndexes.join(", ")}
                </p>
              )}

              {otherSharedRecordings.length > 0 && (
                <p className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-black text-amber-800">
                  Sharing this version will unshare {otherSharedLabel || "the currently shared version"}.
                </p>
              )}
            </div>

            <div className="space-y-3 border-t border-green-100 bg-white/90 p-4">
              <div className="grid grid-cols-2 gap-2">
                {isPlayingAll ? (
                  <button
                    type="button"
                    onClick={stopPlayAllSelected}
                    className="inline-flex h-11 items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-800 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-blue-100"
                  >
                    Stop
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={playAllSelected}
                    disabled={!canPlayAllSelected}
                    className="inline-flex h-11 items-center justify-center rounded-2xl bg-blue-600 px-4 text-sm font-black text-white shadow-lg shadow-blue-600/20 transition hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
                  >
                    Review
                  </button>
                )}

                <button
                  type="button"
                  onClick={createShareLink}
                  disabled={isCreatingShare || selectedAttemptsInOrder.length === 0}
                  className={`inline-flex h-11 items-center justify-center rounded-2xl px-4 text-sm font-black shadow-sm transition focus:outline-none focus:ring-4 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:shadow-none ${
                    shareInfo?.share_url && shareNeedsUpdate
                      ? "border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 focus:ring-amber-100"
                      : "border border-slate-200 bg-white text-slate-800 hover:bg-slate-50 focus:ring-blue-100"
                  }`}
                >
                  {isCreatingShare
                    ? shareInfo?.share_url && shareNeedsUpdate
                      ? "Updating..."
                      : "Checking..."
                    : shareInfo?.share_url && shareNeedsUpdate
                      ? "Update"
                      : shareInfo?.share_url
                        ? "Published"
                        : otherSharedRecordings.length > 0
                          ? "Replace shared"
                          : selectedAttemptsInOrder.length < maxpage
                            ? "Share partial"
                            : "Share"}
                </button>
              </div>

              {playAllStatus && (
                <p className="rounded-2xl border border-blue-100 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700">
                  {playAllStatus}
                </p>
              )}

              {shareInfo?.share_url && (
                <div className="rounded-2xl border border-green-100 bg-green-50/80 px-3 py-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-black text-green-900">
                        {shareInfo.updated ? "Exercise updated" : "Exercise published"}
                      </p>
                      {shareInfo.is_native_speaker && (
                        <span className="rounded-full border border-green-200 bg-white px-2 py-0.5 text-[11px] font-black text-green-700">
                          native speaker
                        </span>
                      )}
                    </div>
                    <p
                      className="mt-1 truncate text-xs font-semibold text-green-800"
                      title={shareInfo.exercise_url || shareInfo.share_url}
                    >
                      {shareInfo.exercise_path || shareInfo.exercise_url || shareInfo.share_url}
                    </p>
                  </div>

                  {shareNeedsUpdate && (
                    <p className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-black text-amber-700">
                      Recording changed · update the exercise.
                    </p>
                  )}

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={openSharedExercise}
                      className="inline-flex h-9 items-center justify-center rounded-xl bg-green-700 px-3 text-xs font-black text-white shadow-sm transition hover:bg-green-800 focus:outline-none focus:ring-4 focus:ring-green-100"
                    >
                      Open ↗
                    </button>
                    <button
                      type="button"
                      onClick={copyShareLink}
                      className="inline-flex h-9 items-center justify-center rounded-xl border border-green-200 bg-white px-3 text-xs font-black text-green-800 shadow-sm transition hover:bg-green-50 focus:outline-none focus:ring-4 focus:ring-green-100"
                    >
                      {shareCopied ? "Copied" : "Copy link"}
                    </button>
                  </div>
                </div>
              )}

              {shareError && (
                <p className="rounded-2xl border border-amber-100 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700">
                  {shareError}
                </p>
              )}
            </div>
          </section>

          <section className="relative overflow-hidden rounded-[30px] border border-blue-100 bg-white shadow-[0_24px_70px_-50px_rgba(37,99,235,0.72)] ring-1 ring-blue-50">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-cyan-400 to-indigo-500" />

            <div className="bg-gradient-to-br from-white via-blue-50/60 to-indigo-50/30 px-5 py-5">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">Sentences</p>
              <h2 className="mt-1 text-lg font-black tracking-tight">
                Current sentence {page}/{maxpage}
              </h2>
            </div>

            <div className="max-h-[48vh] space-y-2 overflow-auto border-t border-blue-100 bg-white/90 p-3">
              {segments.map((text, idx) => {
                const sentenceNumber = idx + 1;
                const sentenceStatus = sentenceStatusFor(sentenceNumber);
                const isCurrentSentence = page === sentenceNumber;
                const attempts = attemptsByIndex[String(sentenceNumber)] || [];

                return (
                  <button
                    key={`sentence-nav-${sentenceNumber}`}
                    type="button"
                    onClick={() => goToPage(sentenceNumber)}
                    disabled={isRecording || loading || isPlayingAll}
                    className={`group grid w-full grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-3 rounded-2xl border px-3 py-2.5 text-left transition ${
                      isCurrentSentence
                        ? "border-blue-300 bg-blue-50 shadow-sm ring-2 ring-blue-100"
                        : "border-slate-200 bg-white hover:border-blue-200 hover:bg-blue-50/60"
                    } disabled:cursor-not-allowed disabled:opacity-70`}
                  >
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-xl border text-sm font-black ${
                        isCurrentSentence
                          ? "border-blue-600 bg-blue-600 text-white"
                          : sentenceStatus.className
                      }`}
                    >
                      {isCurrentSentence ? sentenceNumber : sentenceStatus.short}
                    </span>

                    <span className="min-w-0">
                      <span className="block text-sm font-black text-slate-900">Sentence {sentenceNumber}</span>
                      <span className="block truncate text-xs font-medium text-slate-500">
                        {attempts.length > 0
                          ? `${attempts.length} take${attempts.length === 1 ? "" : "s"}`
                          : "No takes"}
                      </span>
                    </span>

                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-black ${sentenceStatus.className}`}>
                      {sentenceStatus.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        </aside>

        <section className="space-y-5">
          {cleanDescription && (
            <section className="overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm ring-1 ring-blue-50">
              <button
                type="button"
                onClick={() => setShowDescription((value) => !value)}
                className="flex w-full items-center justify-between gap-4 bg-gradient-to-r from-white via-blue-50/30 to-white px-4 py-2.5 text-left transition hover:bg-blue-50"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span className="text-[10px] font-black uppercase tracking-[0.18em] text-blue-600">
                    About
                  </span>
                  <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-black text-blue-700">
                    description added
                  </span>
                </div>

                <span className="shrink-0 text-xs font-black text-slate-500">
                  {showDescription ? "Hide" : "Show"}
                </span>
              </button>

              {showDescription && (
                <div className="border-t border-blue-100 px-5 py-4">
                  <p className="whitespace-pre-wrap text-sm font-semibold leading-7 text-slate-700">
                    {cleanDescription}
                  </p>
                </div>
              )}
            </section>
          )}

          <section className="relative overflow-hidden rounded-[34px] border border-blue-100 bg-white p-5 shadow-[0_24px_70px_-46px_rgba(37,99,235,0.72)] ring-1 ring-blue-50">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-500" />

            <div className="mb-4">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">Read aloud</p>
              <h2 className="mt-1 text-2xl font-black tracking-tight text-slate-950">
                Sentence {page} / {maxpage}
              </h2>
            </div>

            <div className="rounded-[28px] border border-blue-100 bg-gradient-to-br from-blue-50 via-cyan-50/40 to-white px-5 py-6 shadow-inner sm:px-7 sm:py-8">
              <p className="text-xl font-black leading-relaxed tracking-tight text-slate-950 sm:text-2xl">
                {segment || "No sentence available"}
              </p>
            </div>

            <div className="mt-6 rounded-[28px] border border-slate-200 bg-white p-3 shadow-sm">
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:items-center">
                <button
                  type="button"
                  onClick={() => goToPage((current) => current - 1)}
                  disabled={isFirst || isRecording || loading || isPlayingAll}
                  className="inline-flex h-12 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 text-sm font-black text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:shadow-none"
                >
                  ← Previous
                </button>

                <div className="flex flex-wrap items-center justify-center gap-3">
                  {isRecording ? (
                    <button
                      type="button"
                      onClick={stopRecording}
                      className="inline-flex h-14 min-w-[230px] items-center justify-center gap-3 rounded-2xl border border-red-200 bg-white px-7 text-base font-black text-slate-900 shadow-sm transition hover:border-red-300 hover:bg-red-50 focus:outline-none focus:ring-4 focus:ring-red-100"
                    >
                      <span ref={pulseRef} className="flex h-8 w-8 items-center justify-center rounded-full bg-red-50 text-xl text-red-600 ring-1 ring-red-200">
                        <MdMic />
                      </span>
                      Stop recording
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={startRecording}
                      disabled={loading || isPlayingAll || !isgranted || !segment}
                      className="inline-flex h-14 min-w-[250px] items-center justify-center gap-3 rounded-2xl bg-green-600 px-7 text-base font-black text-white shadow-lg shadow-green-600/20 transition hover:bg-green-700 focus:outline-none focus:ring-4 focus:ring-green-100 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-2xl">
                        <MdMicNone />
                      </span>
                      {currentAttempts.length > 0 ? "Record another take" : "Record sentence"}
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => playRecording()}
                    disabled={!canPlay || isPlayingAll}
                    className="inline-flex h-14 min-w-[155px] items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 text-base font-black text-slate-800 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:shadow-none"
                  >
                    {activeAttempt ? "Play sentence" : "Play"}
                  </button>

                  {!isgranted && (
                    <button
                      type="button"
                      onClick={getMicrophonePermission}
                      className="inline-flex h-14 items-center justify-center rounded-2xl bg-slate-950 px-6 text-base font-black text-white shadow-lg shadow-slate-950/20 transition hover:bg-slate-800 focus:outline-none focus:ring-4 focus:ring-slate-200"
                    >
                      Enable microphone
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => goToPage((current) => current + 1)}
                  disabled={isLast || isRecording || loading || isPlayingAll}
                  className="inline-flex h-12 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 text-sm font-black text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:shadow-none"
                >
                  Next →
                </button>
              </div>
            </div>

            <div className="mt-5 border-t border-slate-100 pt-5">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">Used in full recording</p>
                    {currentSentenceState === "selected" && selectedTakeNumber && (
                      <span className="rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-xs font-black text-green-700">
                        take {selectedTakeNumber}
                      </span>
                    )}
                    {currentSentenceState === "needs-choice" && (
                      <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-black text-amber-700">
                        choose one
                      </span>
                    )}
                  </div>

                  {currentAttempts.length > 0 ? (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {currentAttempts.map((attempt, attemptIndex) => {
                        const isSelected = Boolean(attempt.selected);
                        const pronScore = getAttemptPronScore(attempt);
                        const isSelecting = selectingAttemptId === attempt.attempt_id;

                        return (
                          <button
                            key={attempt.attempt_id || `take-mini-${attemptIndex}`}
                            type="button"
                            onClick={() => {
                              if (!isSelected) selectAttempt(attempt);
                            }}
                            disabled={isSelected || isSelecting || isRecording || loading}
                            title={isSelected ? `Take ${attemptIndex + 1} is selected` : `Use take ${attemptIndex + 1}`}
                            className={`inline-flex min-w-[56px] flex-col items-center justify-center rounded-2xl border px-2.5 py-2 text-center transition focus:outline-none focus:ring-4 focus:ring-blue-100 ${
                              isSelected
                                ? "border-blue-300 bg-blue-600 text-white shadow-md shadow-blue-600/20"
                                : "border-slate-200 bg-white text-slate-700 shadow-sm hover:border-blue-200 hover:bg-blue-50"
                            } disabled:cursor-default disabled:opacity-80`}
                          >
                            <span className={`flex h-6 min-w-6 items-center justify-center gap-1 rounded-md px-1.5 text-[11px] font-black ${
                              isSelected ? "bg-white text-blue-700" : "bg-slate-50 text-slate-700 ring-1 ring-slate-200"
                            }`}>
                              {isSelected && <span aria-hidden="true">✓</span>}
                              <span>{attemptIndex + 1}</span>
                            </span>
                            {typeof pronScore === "number" && (
                              <span className={`mt-1 text-[11px] font-black ${isSelected ? "text-blue-50" : metricTone(pronScore)}`}>
                                {pronScore}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="mt-3 text-sm font-medium text-slate-500">No takes yet</p>
                  )}
                </div>

                {currentAttempts.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowTakes((value) => !value)}
                    className={`inline-flex h-11 shrink-0 items-center justify-center rounded-2xl border px-5 text-sm font-black shadow-sm transition focus:outline-none focus:ring-4 focus:ring-blue-100 ${
                      showTakes
                        ? "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        : "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                    }`}
                  >
                    {showTakes ? "Close comparison" : "Compare takes"}
                  </button>
                )}
              </div>

              {showTakes && currentAttempts.length > 0 && (
                <div className="mt-5 rounded-[28px] border border-blue-100 bg-blue-50/40 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">Compare takes</p>
                      {selectedTakeNumber && (
                        <span className="rounded-full border border-blue-200 bg-white px-2.5 py-1 text-xs font-black text-blue-700">
                          using take {selectedTakeNumber}
                        </span>
                      )}
                    </div>
                    {attemptsLoading && (
                      <p className="text-sm font-medium text-slate-500">Loading…</p>
                    )}
                  </div>

                  <div className="mt-4 grid gap-3">
                    {currentAttempts.map((attempt, attemptIndex) => {
                      const pronScore = getAttemptPronScore(attempt);
                      const isSelected = Boolean(attempt.selected);
                      const attemptAudioUrl = resolveAudioUrl(attempt.audio_url);
                      const isSelecting = selectingAttemptId === attempt.attempt_id;

                      return (
                        <article
                          key={attempt.attempt_id}
                          className={`grid gap-3 rounded-3xl border p-4 transition sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center ${
                            isSelected
                              ? "border-blue-200 bg-white shadow-sm ring-2 ring-blue-100"
                              : "border-slate-200 bg-white hover:border-blue-200 hover:bg-blue-50/50"
                          }`}
                        >
                          <div className={`flex h-10 min-w-10 items-center justify-center gap-1 rounded-2xl border px-2 text-sm font-black ${
                            isSelected
                              ? "border-blue-300 bg-blue-600 text-white"
                              : "border-slate-200 bg-slate-50 text-slate-700"
                          }`}>
                            {isSelected && <span aria-hidden="true">✓</span>}
                            <span>{attemptIndex + 1}</span>
                          </div>

                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="font-black text-slate-950">Take {attemptIndex + 1}</h3>
                              {typeof pronScore === "number" && (
                                <span className={`rounded-full px-2.5 py-1 text-xs font-black ring-1 ring-inset ${
                                  pronScore >= 80
                                    ? "bg-green-50 text-green-700 ring-green-200"
                                    : pronScore >= 40
                                      ? "bg-amber-50 text-amber-700 ring-amber-200"
                                      : "bg-red-50 text-red-700 ring-red-200"
                                }`}>
                                  Pron {pronScore}
                                </span>
                              )}
                              {isSelected && (
                                <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-black text-blue-700">
                                  used
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-xs font-medium text-slate-400">{formatAttemptTime(attempt)}</p>
                          </div>

                          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                            <button
                              type="button"
                              onClick={() => playRecording(attemptAudioUrl)}
                              disabled={!attemptAudioUrl || isPlayingAll}
                              className="inline-flex h-10 items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              Play
                            </button>

                            <button
                              type="button"
                              onClick={() => selectAttempt(attempt)}
                              disabled={isSelected || isSelecting}
                              className={`inline-flex h-10 items-center justify-center rounded-2xl px-4 text-sm font-black transition focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed ${
                                isSelected
                                  ? "bg-blue-600 text-white"
                                  : "border border-slate-200 bg-white text-slate-700 hover:bg-blue-50"
                              }`}
                            >
                              {isSelected ? "Selected" : isSelecting ? "Selecting..." : "Use"}
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-5 min-h-[24px] border-t border-slate-100 pt-4">
              {isRecording && (
                <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-2 text-sm font-medium text-red-700">
                  Recording now. Speak naturally, then press Stop.
                </div>
              )}

              {!isRecording && loading && (
                <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700">
                  Saving attempt and analyzing pronunciation…
                </div>
              )}

              {recordingError && (
                <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700">
                  {recordingError}
                </div>
              )}

              {attemptsError && (
                <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700">
                  {attemptsError}
                </div>
              )}
            </div>
          </section>

          <section className="overflow-hidden rounded-[30px] border border-indigo-100 bg-white shadow-[0_20px_55px_-42px_rgba(79,70,229,0.55)] ring-1 ring-indigo-50">
            <button
              type="button"
              onClick={() => setShowFeedback((value) => !value)}
              className="flex w-full items-center justify-between gap-4 bg-gradient-to-r from-white via-indigo-50/50 to-white px-5 py-4 text-left transition hover:bg-indigo-50"
            >
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-indigo-600">AI speech feedback</p>
                <h2 className="mt-1 text-lg font-black tracking-tight text-slate-950">
                  {hasUserAssessment ? `Overall ${typeof overallScore === "number" ? overallScore : ""}` : "Record to see feedback"}
                </h2>
              </div>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-black text-slate-600">
                {showFeedback ? "Hide" : "Show"}
              </span>
            </button>

            {showFeedback && (
              <div className="border-t border-indigo-100 bg-indigo-50/20 p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <p className="text-sm font-semibold leading-6 text-slate-500">
                    Dots show sound-level accuracy. Words stay in sentence order.
                  </p>

                  <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                    {isLong && (
                      <button
                        type="button"
                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                          showOnlyErrors
                            ? "border-red-200 bg-red-50 text-red-700"
                            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                        onClick={() => setShowOnlyErrors((value) => !value)}
                      >
                        {showOnlyErrors ? "Showing errors" : "Only errors"}
                      </button>
                    )}

                    <select
                      className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100"
                      value={viewMode}
                      onChange={(event) => setViewMode(event.target.value)}
                    >
                      <option value="auto">Auto view</option>
                      <option value="chips">Compact chips</option>
                      <option value="grid">Detail grid</option>
                    </select>
                  </div>
                </div>

                {auth?.username === "guest" ? (
                  <div className="mt-5 rounded-3xl border border-amber-100 bg-amber-50 px-5 py-4 text-sm text-amber-700">
                    AI assessment is available for registered users.
                  </div>
                ) : loading ? (
                  <div className="mt-5 flex min-h-[180px] items-center justify-center rounded-3xl border border-slate-200 bg-slate-50">
                    <PacmanLoader loading={loading} color="#2563EB" size={16} />
                  </div>
                ) : !hasUserAssessment ? (
                  <div className="mt-5 rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-5 py-8 text-center">
                    <p className="font-semibold text-slate-950">No AI feedback yet</p>
                    <p className="mt-1 text-sm text-slate-500">Record this sentence to see pronunciation feedback.</p>
                  </div>
                ) : (
                  <div className="mt-5 space-y-5">
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      {Object.entries(userNBest.PronunciationAssessment || {}).map(([key, value]) => (
                        <div key={key} className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{formatKey(key)}</p>
                          <p className={`mt-2 text-3xl font-black ${metricTone(value)}`}>{value}</p>
                        </div>
                      ))}
                    </div>

                    {effectiveView === "chips" ? (
                      <div className="flex flex-wrap gap-2">
                        {visibleWords.map(({ w, idx }) => {
                          const score = w?.PronunciationAssessment?.AccuracyScore;

                          return (
                            <span
                              key={`${idx}-${w?.Word}`}
                              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold ${scoreColor(score)}`}
                            >
                              <span>{w?.Word}</span>
                              <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white ${scoreBadge(score)}`}>
                                {typeof score === "number" ? score : "N/A"}
                              </span>
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="grid gap-3">
                        {visibleWords.map(({ w, idx }) => {
                          const score = w?.PronunciationAssessment?.AccuracyScore;
                          const phonemes = w?.Phonemes || [];

                          return (
                            <article key={`${idx}-${w?.Word}`} className={`rounded-3xl border p-4 ${scoreColor(score)}`}>
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                  <h3 className="text-lg font-black text-slate-950">{w?.Word}</h3>
                                  <p className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                                    {w?.PronunciationAssessment?.ErrorType || "Spoken"}
                                  </p>
                                </div>
                                <span className={`rounded-full px-3 py-1.5 text-sm font-black text-white ${scoreBadge(score)}`}>
                                  {typeof score === "number" ? score : "N/A"}
                                </span>
                              </div>

                              {phonemes.length > 0 && (
                                <div className="mt-4 flex flex-wrap gap-2">
                                  {phonemes.map((phoneme, phonemeIndex) => {
                                    const phonemeScore = phoneme?.PronunciationAssessment?.AccuracyScore;
                                    return (
                                      <span
                                        key={`${idx}-${phoneme?.Phoneme}-${phonemeIndex}`}
                                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700"
                                      >
                                        <span className={`h-2.5 w-2.5 rounded-full border ${phonemeDot(phonemeScore)}`} />
                                        {phoneme?.Phoneme}
                                        <span className="text-slate-400">{typeof phonemeScore === "number" ? phonemeScore : ""}</span>
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </article>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </section>

          <div className="h-6" />
        </section>
      </main>
    </div>
  );

};

export default Slideshow;